import { type AuthId, CalendarId, type EventId } from "@tento-lona/sheets";
import { Json, type JsonObject, RowNetworkKey } from "@tento-lona/sheets";
import type { Network } from "../network.ts";

/**
 * Event operation envelopes shared by the UI and command-line client: the
 * `data` of an event cell write (`POST /api/rows/{rowKey}/cells`), as the
 * JSON the request carries (a `DateTime` is its ISO string).
 */
export namespace EventCellWrite {
  export interface Target {
    rowKey: RowNetworkKey;
    authId: AuthId;
    calendarId: CalendarId;
  }

  /** Use the server-advertised row; retain primary-alias support for older servers. */
  export function target(rowKey: RowNetworkKey, authId: AuthId, calendarId: CalendarId): Target {
    const primaryRow = `:~google:${authId}:calendar:primary:events`;
    const emailRow = `:~google:${authId}:calendar:${authId}:events`;
    // A provider keys an account's primary calendar by the account id.
    const calendar: string = calendarId;
    if (
      (calendar === authId || calendar === "primary") &&
      (rowKey === primaryRow || rowKey === emailRow)
    ) {
      return { rowKey: RowNetworkKey.fromWire(primaryRow), authId, calendarId: CalendarId.of("primary") };
    }
    return { rowKey, authId, calendarId };
  }

  /** Legacy identity fields remain until all supported servers derive them from the row. */
  export function create(target: Target, event: Network.NewEvent): JsonObject {
    return envelope({ authId: target.authId, calendarId: target.calendarId, newEvent: event });
  }

  /**
   * A field patch of the stored event (never a replace). `body` is the
   * write's wire body as `EventPatch.encode` produced it — the one encoder:
   * a set field carries its value, a cleared one `null`, an untouched one
   * no key.
   */
  export function update(target: Target, body: Network.EventUpdate): JsonObject {
    return envelope({ authId: target.authId, calendarId: target.calendarId, update: body });
  }

  /** A copy addresses the destination row and explicitly names its source. */
  export function copy(target: Target, source: Target, eventId: EventId): JsonObject {
    return {
      authId: target.authId, calendarId: target.calendarId,
      sourceAuthId: source.authId, sourceCalendarId: source.calendarId,
      sourceEventId: eventId,
    };
  }

  /** A move addresses the source row and explicitly names its destination. */
  export function move(source: Target, target: Target): JsonObject {
    return {
      authId: source.authId, calendarId: source.calendarId, update: {},
      targetAuthId: target.authId, targetCalendarId: target.calendarId,
    };
  }
}

/** `value` as the JSON object a request body carries for it. */
function envelope(value: { readonly [key: string]: unknown }): JsonObject {
  // `Json.wire` of an object is an object.
  return Json.object(Json.wire(value)) ?? {};
}
